import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import type { ReparsePolicy, ReprocessingStatus } from '@/service/api'

/**
 * How often the page re-reads its status while open.
 *
 * 15s is a deliberate compromise: the underlying sweep only runs every 30 minutes, but
 * the per-row refresh markers clear continuously as workers finish drawings, so the
 * "refreshing now" list is the fast-moving part. Cheap to serve — the endpoint is two
 * indexed queries plus a count.
 */
const STATUS_POLL_MS = 15_000

const SELECT_CLASSES =
  'h-9 w-56 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm ' +
  'focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

/** Absolute timestamp → "3d" / "5h" / "12m", or null when absent. */
function age(iso: string | null): string | null {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

/**
 * FS-637 — Pipeline Reprocessing.
 *
 * When the P&ID pipeline changes in an output-affecting way, every drawing parsed
 * by the old extractor is stale. This page answers the three questions an operator
 * has about that, in one place:
 *
 *   1. Is this tenant behind the current pipeline, and since when?
 *   2. WHICH change did it — the revision and the PR that landed it.
 *   3. Should this tenant re-parse automatically at all?
 *
 * On (3): the default is `auto`, which re-parses only tenants somebody is actually
 * using. That matters because re-parsing is GPU-expensive and most of the fleet is
 * demo / churned / abandoned eval tenants — paying to keep drawings current that
 * nobody will ask about. `never` is for those; `always` is for a tenant that must
 * never serve a stale answer even in a quiet month.
 *
 * A deferred tenant is still reported as BEHIND rather than hidden. That is
 * deliberate: an earlier per-tenant gate on this same pipeline skipped ~280 tenants
 * silently until eval correctness collapsed, so a skip here has to stay visible and
 * countable.
 *
 * Tenant-scoped (JWT). The cross-tenant fleet table is the token-guarded backoffice
 * endpoint — enumerating every org from a tenant JWT would break this API's
 * isolation contract.
 */

export default function PipelineReprocessing() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<ReprocessingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Translated at RENDER time so the load effect has no dependency on `t` — an
  // unstable `t` identity would re-run the effect and clobber a just-saved policy.
  const [error, setError] = useState<'load' | 'save' | null>(null)

  const load = useCallback((opts: { spinner?: boolean } = {}) => {
    let cancelled = false
    // Only the FIRST read shows the spinner. A poll that flips it would make the page
    // flash every interval, which reads as breakage rather than freshness.
    if (opts.spinner !== false) setLoading(true)
    apiService
      .getReprocessingStatus()
      .then((s) => {
        if (!cancelled) {
          setStatus(s)
          setError(null)
        }
      })
      .catch(() => {
        if (!cancelled) setError('load')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Poll, don't read once on mount. Everything on this page changes while it is open
  // and NOTHING here initiates that change: the drift sweep decides, the fan-out
  // enqueues, and the Python workers clear each row's marker. A mount-only read left
  // the verdict, the refreshing count and the drawing list frozen at whatever they
  // were when the tab was opened — worst exactly when an operator is watching a live
  // re-parse, which is the reason to open it at all.
  useEffect(() => {
    const stopInitial = load()
    const timer = setInterval(() => load({ spinner: false }), STATUS_POLL_MS)
    return () => {
      stopInitial()
      clearInterval(timer)
    }
  }, [load])

  const onPolicyChange = useCallback(
    async (policy: ReparsePolicy) => {
      setSaving(true)
      try {
        await apiService.setReprocessingPolicy(policy)
        // Re-read rather than patching locally: changing the policy can change the
        // DEFERRAL too (a `never` tenant that is behind becomes deferred), and that
        // is computed server-side.
        await apiService.getReprocessingStatus().then(setStatus)
        setError(null)
      } catch {
        setError('save')
      } finally {
        setSaving(false)
      }
    },
    [],
  )

  if (loading && !status) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="pipeline-reprocessing-loading">
        {t('common:pipeline_reprocessing.loading', 'Loading…')}
      </div>
    )
  }

  if (error === 'load' || !status) {
    return (
      <div className="p-6 text-sm text-destructive" data-testid="pipeline-reprocessing-error">
        {t(
          'common:pipeline_reprocessing.load_error',
          "Couldn't load reprocessing status. Reload the page to try again.",
        )}
      </div>
    )
  }

  const behind = status.parse_verdict === 'behind'
  const behindAge = age(status.behind_since)
  const activityAge = age(status.last_activity_at)

  return (
    <div className="space-y-6 p-6" data-testid="pipeline-reprocessing">
      {/* No VISIBLE <h1>: the settings topbar already names this section —
          repeating it read as a double heading. sr-only keeps it reachable
          via heading navigation; the subtitle carries the visible context. */}
      <header className="space-y-1">
        <h1 className="sr-only">{t('common:pipeline_reprocessing.title', 'Pipeline Reprocessing')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'common:pipeline_reprocessing.subtitle',
            'When the P&ID pipeline changes, existing drawings are re-parsed so answers reflect the current extractor. Documents stay available and answerable throughout.',
          )}
        </p>
      </header>

      {/* ---- state of this tenant ---- */}
      <section className="rounded-lg border p-4" data-testid="pipeline-status-card">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={
              behind
                ? 'inline-flex items-center rounded-md border border-yellow-400 bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-900 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                : 'inline-flex items-center rounded-md border border-green-400 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800 dark:border-green-700 dark:bg-green-900/20 dark:text-green-300'
            }
            data-testid="pipeline-verdict"
          >
            {behind
              ? t('common:pipeline_reprocessing.behind', 'Behind current pipeline')
              : t('common:pipeline_reprocessing.current', 'Up to date')}
          </span>
          {behind && behindAge && (
            <span className="text-xs text-muted-foreground" data-testid="pipeline-behind-since">
              {t('common:pipeline_reprocessing.behind_since', {
                age: behindAge,
                defaultValue: 'for {{age}}',
              })}
            </span>
          )}
          {/* A deferral is shown, never hidden — a silent skip is the failure this
              feature exists to prevent. */}
          {status.deferred_reason && (
            <span
              className="inline-flex items-center rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-800/40 dark:text-slate-300"
              data-testid="pipeline-deferred"
            >
              {status.deferred_reason === 'inactive'
                ? t(
                    'common:pipeline_reprocessing.deferred_inactive',
                    'Deferred — tenant idle, no GPU spent',
                  )
                : status.deferred_reason === 'retry_exhausted'
                  ? t(
                      'common:pipeline_reprocessing.deferred_retry_exhausted',
                      'Stopped — some drawings failed to re-parse after several attempts',
                    )
                  : t('common:pipeline_reprocessing.deferred_policy', 'Deferred — policy is Never')}
            </span>
          )}
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">
              {t('common:pipeline_reprocessing.revision', 'Current pipeline revision')}
            </dt>
            <dd className="font-mono text-xs" data-testid="pipeline-revision">
              {status.pipeline_revision ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              {t('common:pipeline_reprocessing.pr', 'Change that landed it')}
            </dt>
            <dd data-testid="pipeline-pr">
              {status.pipeline_pr ? (
                <a
                  className="text-primary underline"
                  href={status.pipeline_pr}
                  target="_blank"
                  rel="noreferrer"
                >
                  {status.pipeline_pr.replace(/^.*\/pull\//, '#')}
                </a>
              ) : (
                <span className="text-muted-foreground">
                  {t('common:pipeline_reprocessing.no_pr', 'Not recorded')}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              {t('common:pipeline_reprocessing.drawings', 'Drawings in this tenant')}
            </dt>
            <dd data-testid="pipeline-drawing-count">{status.drawing_count}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              {t('common:pipeline_reprocessing.last_activity', 'Last used')}
            </dt>
            <dd data-testid="pipeline-last-activity">
              {activityAge
                ? t('common:pipeline_reprocessing.ago', {
                    age: activityAge,
                    defaultValue: '{{age}} ago',
                  })
                : t('common:pipeline_reprocessing.never_used', 'No recorded activity')}
            </dd>
          </div>
        </dl>

        {status.pipeline_why && (
          <p className="mt-4 border-l-2 pl-3 text-xs text-muted-foreground" data-testid="pipeline-why">
            {status.pipeline_why}
          </p>
        )}
      </section>

      {/* ---- the on/off control ---- */}
      <section className="rounded-lg border p-4">
        <label className="block text-sm font-medium" htmlFor="reparse-policy">
          {t('common:pipeline_reprocessing.policy_label', 'Automatic reprocessing')}
        </label>
        <p className="mt-1 text-xs text-muted-foreground">
          {t(
            'common:pipeline_reprocessing.policy_help',
            'Auto re-parses only while this tenant is in use, so an idle tenant costs nothing. It is still reported as behind either way.',
          )}
        </p>
        <select
          id="reparse-policy"
          className={`${SELECT_CLASSES} mt-3`}
          value={status.policy}
          disabled={saving}
          data-testid="reparse-policy-select"
          onChange={(e) => void onPolicyChange(e.target.value as ReparsePolicy)}
        >
          <option value="auto">
            {t('common:pipeline_reprocessing.policy_auto', 'Auto — only when in use (recommended)')}
          </option>
          <option value="always">
            {t('common:pipeline_reprocessing.policy_always', 'Always — even when idle')}
          </option>
          <option value="never">
            {t('common:pipeline_reprocessing.policy_never', 'Never — do not spend GPU here')}
          </option>
        </select>
        {/*
          States the one thing this control does NOT do. A change applies within
          seconds (the write nudges the worker to re-evaluate rather than waiting out
          its interval), but it only governs what starts NEXT — nothing cancels
          drawings already queued, so `never` does not abort a running re-parse. An
          admin flipping it to stop a fan-out in progress would otherwise reasonably
          expect it to.
        */}
        <p className="mt-2 text-xs text-muted-foreground" data-testid="pipeline-policy-scope">
          {t(
            'common:pipeline_reprocessing.policy_scope',
            'Applies within seconds, and governs what starts next — drawings already refreshing will finish.',
          )}
        </p>
        {error === 'save' && (
          <p className="mt-2 text-xs text-destructive" data-testid="pipeline-save-error">
            {t(
              'common:pipeline_reprocessing.save_error',
              "Couldn't save. Pick the option again to retry.",
            )}
          </p>
        )}
      </section>

      {/* ---- what is refreshing right now ---- */}
      <section className="rounded-lg border p-4">
        <h2 className="text-sm font-medium">
          {t('common:pipeline_reprocessing.refreshing_title', 'Refreshing now')}{' '}
          <span className="text-muted-foreground" data-testid="pipeline-refreshing-count">
            ({status.refreshing_count})
          </span>
        </h2>
        {status.refreshing.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="pipeline-refreshing-empty">
            {t(
              'common:pipeline_reprocessing.refreshing_empty',
              'Nothing is being re-parsed right now.',
            )}
          </p>
        ) : (
          <ul className="mt-3 divide-y text-sm">
            {status.refreshing.map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
                <span className="font-medium">{r.original_filename}</span>
                {age(r.reprocessing_started_at) && (
                  <span className="text-xs text-muted-foreground">
                    {t('common:pipeline_reprocessing.started_ago', {
                      age: age(r.reprocessing_started_at),
                      defaultValue: 'started {{age}} ago',
                    })}
                  </span>
                )}
                {r.reprocess_reason && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {r.reprocess_reason}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
